import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import {
  fetchActiveBudget as fetchActiveBudgetApi,
  fetchActiveBudgetTransactions,
  fetchBudgetTree,
} from '@/api/budget';
import type { QueryParams, RequestOptions } from '@/api/client';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { convertCurrency } from '@/api/product';
import type { IProductToSave } from '@/types/product';
import type {
  IActiveBudget,
  IActiveBudgetTransaction,
  IBudgetTree,
  IBudgetTreeNode,
  IRemainingBudget,
  IVirtualBudgetTreeNode,
} from '@/types/budget';

interface IReservedVBudget {
  vBudgetId: number;
  reservedBudget: number;
}

export const useActiveBudgetStore = defineStore('activeBudget', () => {
  const userStore = useUserStore();
  const featuresStore = useCustomerFeaturesStore();
  const activeBudget = ref<IActiveBudget | null>(null);
  const vBudgetsTree = ref<IBudgetTree | null>(null);
  const budgetTransactions = ref<IActiveBudgetTransaction[]>([]);
  const reservedVBudgets = ref<Record<number, number>>({});

  const loadingActiveBudget = ref(true);
  const loadingVBudgets = ref(false);
  const loadingBudgetTransactions = ref(false);

  const noActiveBudget = ref(false);

  // only customers bound by budget must have an active budget to create orders; for everyone
  // else a missing active budget is expected and must not gate the order-creation flow.
  const budgetGateActive = computed(() => featuresStore.featureBoundByBudget && noActiveBudget.value);

  const activeBudgetId = computed(() => activeBudget.value?.id ?? null);
  const remainingBudget = computed<IRemainingBudget | null>(() => activeBudget.value?.remaining_budget ?? null);

  async function getActiveBudget(params: QueryParams = {}, requestOptions: RequestOptions = {}) {
    loadingActiveBudget.value = true;

    try {
      const response = await fetchActiveBudgetApi(params, requestOptions);

      activeBudget.value = response?.data ?? null;
      noActiveBudget.value = !activeBudget.value?.id;

      return activeBudget.value;
    } catch (error) {
      activeBudget.value = null;
      noActiveBudget.value = true;
      throw error;
    } finally {
      loadingActiveBudget.value = false;
    }
  }

  async function waitForActiveBudgetLoading() {
    if (!loadingActiveBudget.value) return;

    await new Promise<void>((resolve) => {
      const stop = watch(
        loadingActiveBudget,
        (isLoading) => {
          if (!isLoading) {
            stop();
            resolve();
          }
        },
        { immediate: true }
      );
    });
  }

  async function getVBudgetsTree(params: QueryParams = {}, customerBudgetId?: number) {
    const budgetId = customerBudgetId ?? activeBudgetId.value;
    if (!budgetId) {
      vBudgetsTree.value = null;
      return null;
    }

    loadingVBudgets.value = true;

    try {
      const response = await fetchBudgetTree(budgetId, params);
      vBudgetsTree.value = response?.data ?? null;
      return vBudgetsTree.value;
    } finally {
      loadingVBudgets.value = false;
    }
  }

  async function getBudgetTransactions(params: QueryParams = {}) {
    loadingBudgetTransactions.value = true;

    try {
      const response = await fetchActiveBudgetTransactions(params);
      budgetTransactions.value = response?.data ?? [];
      return budgetTransactions.value;
    } finally {
      loadingBudgetTransactions.value = false;
    }
  }

  function resolveBudgetNodeId(vBudgetId: number | undefined, type?: string): number {
    if (type === 'customer_budget') return 0;
    if (vBudgetId && vBudgetId > 0) return vBudgetId;

    return 0;
  }

  async function toReservedVBudgetAmount(product: IProductToSave, targetCurrency: string) {
    // prefer the budget+options total; fall back to budget for products saved without it (e.g. loaded orders)
    const rawAmount = product.totalPrice ?? product.budget ?? product.spending_limit ?? 0;
    const fromCurrency = product.currency;

    const amount =
      fromCurrency && targetCurrency && fromCurrency !== targetCurrency
        ? (await convertCurrency(rawAmount, fromCurrency, targetCurrency)).amount
        : rawAmount;

    return {
      vBudgetId: resolveBudgetNodeId(product.vBudget?.id, product.vBudget?.type),
      amount,
    };
  }

  async function syncReservedVBudgetsFromProducts(productsToSave: IProductToSave[]) {
    const next: Record<number, number> = {};
    const targetCurrency = userStore.currentCustomer?.currency || 'CHF';

    const conversions = productsToSave
      .filter((product) => product.vBudget?.id)
      .map((product) => toReservedVBudgetAmount(product, targetCurrency));

    const results = await Promise.all(conversions);

    for (const { vBudgetId, amount } of results) {
      next[vBudgetId] = (next[vBudgetId] ?? 0) + amount;
    }

    reservedVBudgets.value = next;
  }

  function listReservedVBudgets(): IReservedVBudget[] {
    return Object.entries(reservedVBudgets.value).map(([vBudgetId, reservedBudget]) => ({
      vBudgetId: Number(vBudgetId),
      reservedBudget,
    }));
  }

  async function changeReservedVBudget(productToSave: IProductToSave) {
    // Convert to the user currency
    const userCurrencyAmount = await toReservedVBudgetAmount(
      productToSave,
      userStore.currentCustomer?.currency || 'CHF'
    );

    const futureReserved = (reservedVBudgets.value[userCurrencyAmount.vBudgetId] ?? 0) + userCurrencyAmount.amount;

    reservedVBudgets.value = {
      ...reservedVBudgets.value,
      [userCurrencyAmount.vBudgetId]: futureReserved,
    };
  }

  async function deleteReservedVBudget(vBudgetId: number | undefined, productToSave?: IProductToSave) {
    const vBudgetIdNum = resolveBudgetNodeId(vBudgetId);
    if (typeof reservedVBudgets.value[vBudgetIdNum] === 'undefined') return;

    if (productToSave) {
      const userVBudget = await toReservedVBudgetAmount(productToSave, userStore.currentCustomer?.currency || 'CHF');

      const futureReserved = (reservedVBudgets.value[userVBudget.vBudgetId] ?? 0) - userVBudget.amount;

      reservedVBudgets.value = {
        ...reservedVBudgets.value,
        [userVBudget.vBudgetId]: Math.max(0, futureReserved),
      };
    } else {
      delete reservedVBudgets.value[vBudgetIdNum];
    }
  }

  function resolveBudgetRemainingFromTree(vBudgetId: number | undefined): number | null {
    if (!vBudgetsTree.value?.tree) return null;
    const resolvedVBudgetId = resolveBudgetNodeId(vBudgetId);

    const findInNode = (node: IBudgetTreeNode | IVirtualBudgetTreeNode): number | null => {
      if (
        node.type === 'customer_budget' &&
        'customer_budget' in node &&
        node.customer_budget?.data?.id === resolvedVBudgetId
      ) {
        return node.availability?.available_unallocated ?? 0;
      }

      if ('vbudget' in node && node.vbudget?.data?.id === resolvedVBudgetId) {
        return node.availability?.available_unallocated ?? 0;
      }

      for (const child of node.children || []) {
        const found = findInNode(child);
        if (found !== null) return found;
      }

      return null;
    };

    return findInNode(vBudgetsTree.value.tree);
  }

  function getRemainingBudgetAfterReserved(vBudgetId: number | undefined, baseRemainingBudget?: number) {
    const resolvedVBudgetId = resolveBudgetNodeId(vBudgetId);
    const resolvedRemaining =
      typeof baseRemainingBudget === 'number'
        ? baseRemainingBudget
        : (resolveBudgetRemainingFromTree(resolvedVBudgetId) ?? 0);
    const reservedBudget = reservedVBudgets.value[resolvedVBudgetId] ?? 0;

    return Math.max(0, resolvedRemaining - reservedBudget);
  }

  function clearActiveBudgetState() {
    activeBudget.value = null;
    vBudgetsTree.value = null;
    budgetTransactions.value = [];
    reservedVBudgets.value = {};
    noActiveBudget.value = false;
    loadingActiveBudget.value = true;
  }

  return {
    activeBudget,
    vBudgetsTree,
    budgetTransactions,
    reservedVBudgets,
    loadingActiveBudget,
    loadingVBudgets,
    loadingBudgetTransactions,
    noActiveBudget,
    budgetGateActive,
    activeBudgetId,
    remainingBudget,
    getActiveBudget,
    waitForActiveBudgetLoading,
    getVBudgetsTree,
    getBudgetTransactions,
    listReservedVBudgets,
    syncReservedVBudgetsFromProducts,
    changeReservedVBudget,
    deleteReservedVBudget,
    getRemainingBudgetAfterReserved,
    clearActiveBudgetState,
  };
});
